import { useEffect, useState } from "react";
import { Send, Loader2, Calendar, FileText, Stamp } from "lucide-react";
import { toast } from "sonner";
import api, { formatEzDate } from "../../../lib/api";
import { Button } from "../../ui/button";
import { Input } from "../../ui/input";
import { Label } from "../../ui/label";
import { Textarea } from "../../ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../ui/dialog";
import { DispoBackButton } from "./DispoBackButton";
import { FeatureGuideTriangle } from "./FeatureGuideTriangle";
import { DispoGearButton, ShippingSettingsDialog } from "./DispoAutomationSettings";
import { previewPdfResponse, previewPdfBlob } from "../../../lib/pdfPreview";

// Feb-2026 (User-Wunsch) — Empfänger-Typen & versendbare Unterlagen.
const RECIPIENTS = [
    { type: "kaeufer", label: "Käufer", needsName: false },
    { type: "zulassungsdienst", label: "Zulassungsdienst", needsName: true },
    { type: "zulassungsstelle", label: "Zulassungsstelle", needsName: true },
    { type: "bank_leasing", label: "Bank / Leasing", needsName: true },
];
const DOCS = [
    { key: "coc", label: "COC (Konformitätserklärung)" },
    { key: "zb1", label: "ZB1 (Zulassung)" },
    { key: "zb2", label: "ZB2 (Kraftfahrzeugbrief)" },
    { key: "ausweis_pass", label: "Ausweis / Pass" },
    { key: "zulassungsantrag", label: "Zulassungsantrag" },
    { key: "sepa_kfz_steuer", label: "SEPA Kraftfahrzeugsteuer" },
    { key: "evb_nummer", label: "EVB-Nummer" },
    { key: "versicherungskennzeichen", label: "Versicherungskennzeichen" },
    { key: "handelsregisterauszug", label: "Handelsregisterauszug" },
    { key: "gewerbeanmeldung", label: "Gewerbeanmeldung" },
    { key: "tuev_abnahme", label: "TÜV-Abnahme" },
    { key: "meldebescheinigung", label: "Meldebescheinigung" },
];

// Deutsche-Post-Internetmarke-Produkte (Juni-2026, User-Wunsch).
const POSTAGE_PRODUCTS = [
    { value: "standardbrief", label: "Standardbrief (bis 20 g)" },
    { value: "kompaktbrief", label: "Kompaktbrief (bis 50 g)" },
    { value: "grossbrief", label: "Großbrief (bis 500 g)" },
    { value: "maxibrief", label: "Maxibrief (bis 1000 g)" },
    { value: "einschreiben_einwurf", label: "Einschreiben Einwurf" },
    { value: "einschreiben", label: "Einschreiben" },
    { value: "einschreiben_rueckschein", label: "Einschreiben + Rückschein" },
];

function initRecipients(existing) {
    const map = {};
    RECIPIENTS.forEach((r) => { map[r.type] = { checked: false, name: "", sent_at: "" }; });
    (existing?.recipients || []).forEach((r) => {
        if (map[r.type]) map[r.type] = { checked: true, name: r.name || "", sent_at: r.sent_at || "" };
    });
    return map;
}

// ---------- Papiere versenden -------------------------------------------------
export function PapersShipmentModal({ listingId, existing, onBack, onClose, onSaved, selfRegistration = false, customer = null }) {
    const [shipSettingsOpen, setShipSettingsOpen] = useState(false);
    const pdfUrl = `${process.env.REACT_APP_BACKEND_URL}/api/listings/${listingId}/dispo/papers-shipment/cover-letter.pdf?ts=${Date.now()}`;
    const [recipients, setRecipients] = useState(() => initRecipients(existing));
    const [documents, setDocuments] = useState(() => (existing?.documents || []));
    const [saved, setSaved] = useState({});
    const [carrier, setCarrier] = useState(existing?.carrier || "");
    const [tracking, setTracking] = useState(existing?.tracking_number || "");
    const [cover, setCover] = useState(existing?.cover_letter || "");
    const [saving, setSaving] = useState(false);
    // Jul-2026 (User-Wunsch): Zahlungsarten + Zahlungseingang direkt im Versand sichtbar
    const [payment, setPayment] = useState(null);
    // Deutsche Post Internetmarke (Porto/Einschreiben)
    const [imProduct, setImProduct] = useState("einschreiben_einwurf");
    const [imRec, setImRec] = useState({ name: "", street: "", postal_code: "", city: "" });
    // Jul-2026 (User-Wunsch): Empfänger automatisch aus dem Kundenprofil übernehmen.
    useEffect(() => {
        if (!customer) return;
        setImRec((p) => (p.name || p.street) ? p : {
            name: [customer.first_name, customer.last_name].filter(Boolean).join(" ") || customer.company_name || "",
            street: [customer.street, customer.street_no].filter(Boolean).join(" "),
            postal_code: customer.postal_code || "",
            city: customer.city || "",
        });
    }, [customer]);
    const [imLoading, setImLoading] = useState(false);
    const [imMocked, setImMocked] = useState(null);

    useEffect(() => {
        api.get("/dealer/papers-recipients").then(({ data }) => {
            const grp = {};
            (data.recipients || []).forEach((r) => {
                grp[r.type] = grp[r.type] || [];
                if (r.name && !grp[r.type].includes(r.name)) grp[r.type].push(r.name);
            });
            setSaved(grp);
        }).catch(() => setSaved({}));
        api.get(`/listings/${listingId}/dispo/payment`).then(({ data }) => setPayment(data)).catch(() => setPayment(null));
    }, [listingId]);

    const toggleRecipient = (type) => setRecipients((p) => {
        const cur = p[type];
        const checked = !cur.checked;
        return { ...p, [type]: { ...cur, checked, sent_at: checked ? (cur.sent_at || new Date().toISOString()) : "" } };
    });
    const setRecipientName = (type, name) => setRecipients((p) => ({ ...p, [type]: { ...p[type], name } }));
    const toggleDoc = (key) => setDocuments((p) => (p.includes(key) ? p.filter((x) => x !== key) : [...p, key]));

    const submit = async () => {
        const recArr = RECIPIENTS
            .filter((r) => recipients[r.type].checked)
            .map((r) => ({ type: r.type, name: recipients[r.type].name, sent_at: recipients[r.type].sent_at }));
        if (recArr.length === 0 && documents.length === 0 && !tracking.trim()) {
            toast.error("Bitte mindestens einen Empfänger oder eine Unterlage auswählen.");
            return;
        }
        setSaving(true);
        try {
            await api.post(`/listings/${listingId}/dispo/papers-shipment`, {
                recipients: recArr, documents, carrier, tracking_number: tracking, cover_letter: cover,
            });
            toast.success("Papier-Versand erfasst");
            // Begleitschreiben-PDF in der globalen Vorschau anzeigen (revisionssicher am Inserat gespeichert).
            try {
                const r = await api.get(`/listings/${listingId}/dispo/papers-shipment/cover-letter.pdf`, { responseType: "blob" });
                previewPdfResponse(r, "Begleitschreiben.pdf");
            } catch { /* PDF optional */ }
            onSaved?.();
            onClose();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Erfassung fehlgeschlagen");
        } finally { setSaving(false); }
    };

    const createPostage = async () => {
        if (!imRec.name.trim() || !imRec.street.trim() || !imRec.postal_code.trim()) {
            toast.error("Bitte Empfänger-Name, Straße und PLZ angeben.");
            return;
        }
        setImLoading(true);
        try {
            const { data } = await api.post(`/listings/${listingId}/dispo/papers-shipment/internetmarke`, {
                product: imProduct, recipient: imRec, weight_g: undefined,
            });
            setCarrier("Deutsche Post Internetmarke");
            setTracking(data.tracking_number);
            setImMocked(data.mocked);
            const raw = atob(data.label_base64);
            const arr = new Uint8Array(raw.length);
            for (let i = 0; i < raw.length; i++) arr[i] = raw.charCodeAt(i);
            previewPdfBlob(new Blob([arr], { type: "application/pdf" }), `Porto-Label_${data.tracking_number}.pdf`);
            toast.success(`Porto-Label erstellt · ${data.tracking_number}${data.mocked ? " (TESTMODUS)" : ""}`);
        } catch (e) {
            toast.error(e.response?.data?.detail || "Porto-Label konnte nicht erstellt werden");
        } finally { setImLoading(false); }
    };

    return (
        <Dialog open onOpenChange={(o) => !o && onClose()}>
            <DialogContent onPointerDownOutside={(e) => e.preventDefault()} onInteractOutside={(e) => e.preventDefault()} className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="dispo-modal-papers">
                <FeatureGuideTriangle guideKey="papers" />
                <DialogHeader><DialogTitle className="flex items-center gap-2"><Send className="w-5 h-5" /> Papiere versenden
                    <DispoGearButton onClick={() => setShipSettingsOpen(true)} testid="shipping-settings-gear" title="Versand-Regeln & Postversand-Zugangsdaten" />
                </DialogTitle></DialogHeader>
                <ShippingSettingsDialog open={shipSettingsOpen} onClose={() => setShipSettingsOpen(false)} listingId={listingId} />
                <div className="text-[11px] text-gray-600 mb-1 flex items-center gap-1"><Calendar className="w-3 h-3" /> Beim Anhaken eines Empfängers wird automatisch das Versanddatum hinterlegt.</div>

                {/* Selbstzulassung-Hinweis (Jul-2026, User-Wunsch) */}
                {selfRegistration && (
                    <div className="bg-blue-50 border border-blue-200 rounded-sm px-3 py-2 text-[12px] text-blue-900" data-testid="dispo-pap-selfreg-hint">
                        <strong>Selbstzulassung durch Kunde</strong> — der Zulassungsprozess entfällt. Es erfolgt nur der Versand der Fahrzeugpapiere an den Kunden bzw. die finanzierende Bank.
                    </div>
                )}

                {/* Zahlungsstatus vor Versand (Jul-2026, User-Wunsch) */}
                {payment && (
                    <div className={`rounded-sm border p-2.5 ${Number(payment.balance) <= 0.005 ? "border-emerald-300 bg-emerald-50" : "border-red-300 bg-red-50"}`} data-testid="dispo-pap-payment-status">
                        <div className="text-[10px] font-bold uppercase tracking-wider text-gray-600 mb-1">Zahlung vor Versand prüfen</div>
                        <div className="flex flex-wrap gap-x-4 gap-y-0.5 text-[12px]">
                            {(payment.payments || []).map((p, i) => (
                                <span key={i} className="text-gray-700">
                                    {({ bar: "Barzahlung", ueberweisung: "Überweisung", ec_karte: "EC-Karte", finanzierung: "Finanzierung", leasing: "Leasing", anzahlung: "Anzahlung", inzahlungnahme: "Inzahlungnahme" }[p.method] || p.method)}: <strong className="font-mono-num">{Number(p.amount || 0).toLocaleString("de-DE", { style: "currency", currency: "EUR" })}</strong>
                                </span>
                            ))}
                        </div>
                        <div className={`text-[12px] font-bold mt-1 ${Number(payment.balance) <= 0.005 ? "text-emerald-700" : "text-red-700"}`}>
                            {Number(payment.balance) <= 0.005
                                ? "✓ Zahlung vollständig eingegangen — Versand freigegeben"
                                : `⚠ Offener Restbetrag: ${Number(payment.balance || 0).toLocaleString("de-DE", { style: "currency", currency: "EUR" })} — Versand erst nach Zahlungseingang empfohlen`}
                        </div>
                    </div>
                )}

                {/* Empfänger */}
                <div data-testid="dispo-pap-recipients">
                    <Label className="text-xs font-bold uppercase tracking-wider text-gray-500">Empfänger</Label>
                    <div className="mt-1.5 space-y-2">
                        {RECIPIENTS.map((r) => {
                            const st = recipients[r.type];
                            return (
                                <div key={r.type} className={`rounded-sm border p-2.5 transition-colors ${st.checked ? "border-[#FFCC00] bg-[#FFFBEB]" : "border-gray-200"}`}>
                                    <div className="flex items-center gap-2 flex-wrap">
                                        <label className="flex items-center gap-2 text-sm font-bold cursor-pointer shrink-0">
                                            <input type="checkbox" checked={st.checked} onChange={() => toggleRecipient(r.type)} className="accent-[#111827]" data-testid={`dispo-pap-rec-${r.type}`} />
                                            {r.label}
                                        </label>
                                        {st.checked && r.needsName && (
                                            <>
                                                <Input value={st.name} onChange={(e) => setRecipientName(r.type, e.target.value)} placeholder="Empfänger / Adresse" list={`saved-${r.type}`} className="h-8 text-sm flex-1 min-w-[180px]" data-testid={`dispo-pap-recname-${r.type}`} />
                                                <datalist id={`saved-${r.type}`}>
                                                    {(saved[r.type] || []).map((n) => <option key={n} value={n} />)}
                                                </datalist>
                                            </>
                                        )}
                                        {st.checked && st.sent_at && (
                                            <span className="text-[11px] text-gray-500 font-mono-num ml-auto" data-testid={`dispo-pap-recdate-${r.type}`}>versendet {formatEzDate(st.sent_at)}</span>
                                        )}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>

                {/* Versendete Unterlagen */}
                <div className="mt-3" data-testid="dispo-pap-documents">
                    <Label className="text-xs font-bold uppercase tracking-wider text-gray-500">Versendete Unterlagen</Label>
                    <div className="mt-1.5 grid grid-cols-1 sm:grid-cols-2 gap-1.5">
                        {DOCS.map((d) => (
                            <label key={d.key} className={`flex items-center gap-2 text-sm cursor-pointer rounded-sm border px-2.5 py-1.5 transition-colors ${documents.includes(d.key) ? "border-emerald-300 bg-emerald-50" : "border-gray-200"}`}>
                                <input type="checkbox" checked={documents.includes(d.key)} onChange={() => toggleDoc(d.key)} className="accent-emerald-600" data-testid={`dispo-pap-doc-${d.key}`} />
                                {d.label}
                            </label>
                        ))}
                    </div>
                </div>

                {/* Deutsche Post Internetmarke (Porto / Einschreiben) — Juni-2026 */}
                <div className="mt-3 rounded-sm border border-amber-200 bg-amber-50/50 p-3" data-testid="dispo-pap-internetmarke">
                    <div className="flex items-center gap-2 mb-2">
                        <Stamp className="w-4 h-4 text-amber-700" />
                        <span className="text-xs font-bold uppercase tracking-wider text-amber-900">Deutsche Post Internetmarke</span>
                        {imMocked && <span className="text-[10px] font-bold uppercase bg-amber-200 text-amber-900 px-1.5 py-0.5 rounded-sm" data-testid="dispo-pap-im-mockbadge">Testmodus</span>}
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                        <div className="sm:col-span-2">
                            <Label className="text-[11px] text-amber-900">Porto-Produkt</Label>
                            <select
                                value={imProduct}
                                onChange={(e) => setImProduct(e.target.value)}
                                className="mt-1 h-9 w-full text-sm border border-amber-300 rounded-sm px-2 bg-white"
                                data-testid="dispo-pap-im-product"
                            >
                                {POSTAGE_PRODUCTS.map((p) => <option key={p.value} value={p.value}>{p.label}</option>)}
                            </select>
                        </div>
                        <div><Label className="text-[11px] text-amber-900">Empfänger-Name</Label><Input value={imRec.name} onChange={(e) => setImRec((p) => ({ ...p, name: e.target.value }))} className="mt-1 h-9" placeholder="Vor- und Nachname" data-testid="dispo-pap-im-name" /></div>
                        <div><Label className="text-[11px] text-amber-900">Straße + Nr.</Label><Input value={imRec.street} onChange={(e) => setImRec((p) => ({ ...p, street: e.target.value }))} className="mt-1 h-9" placeholder="Musterweg 5" data-testid="dispo-pap-im-street" /></div>
                        <div><Label className="text-[11px] text-amber-900">PLZ</Label><Input value={imRec.postal_code} onChange={(e) => setImRec((p) => ({ ...p, postal_code: e.target.value }))} className="mt-1 h-9 font-mono-num" placeholder="80331" data-testid="dispo-pap-im-plz" /></div>
                        <div><Label className="text-[11px] text-amber-900">Ort</Label><Input value={imRec.city} onChange={(e) => setImRec((p) => ({ ...p, city: e.target.value }))} className="mt-1 h-9" placeholder="München" data-testid="dispo-pap-im-city" /></div>
                    </div>
                    <Button type="button" onClick={createPostage} disabled={imLoading} className="mt-2 h-9 rounded-sm bg-amber-600 hover:bg-amber-700 text-white text-xs font-bold uppercase tracking-wider" data-testid="dispo-pap-im-create">
                        {imLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Stamp className="w-4 h-4 mr-1.5" /> Porto-Label erstellen</>}
                    </Button>
                    <p className="text-[10px] text-amber-800/80 mt-1.5">Erstellt ein Briefporto-/Einschreiben-Label inkl. Sendungsnummer. Versender &amp; Versendungsnummer werden automatisch übernommen.</p>
                </div>

                {/* Versand-Details (optional) */}
                <div className="mt-3 grid grid-cols-2 gap-3">
                    <div><Label className="text-xs">Versender (optional)</Label><Input value={carrier} onChange={(e) => setCarrier(e.target.value)} placeholder="z. B. DHL / Eigenübergabe" className="h-9" data-testid="dispo-pap-carrier" /></div>
                    <div><Label className="text-xs">Versendungsnummer (optional)</Label><Input value={tracking} onChange={(e) => setTracking(e.target.value)} className="h-9 font-mono-num" placeholder="z. B. DHL00123456789" data-testid="dispo-pap-tracking" /></div>
                </div>
                <div className="mt-2"><Label className="text-xs">Anschreiben-Text (optional)</Label><Textarea rows={3} value={cover} onChange={(e) => setCover(e.target.value)} placeholder="Optionaler Anschreiben-Text…" data-testid="dispo-pap-letter" /></div>

                <DialogFooter className="gap-2">
                    <DispoBackButton onBack={onBack} />
                    {existing && (
                        <a href={pdfUrl} target="_blank" rel="noopener noreferrer" data-testid="dispo-pap-pdf-link"
                            className="inline-flex items-center text-sm font-bold text-[#111827] hover:underline mr-auto">
                            <FileText className="w-4 h-4 mr-1" /> Begleitschreiben (PDF)
                        </a>
                    )}
                    <Button variant="outline" onClick={onClose}>Abbrechen</Button>
                    <Button onClick={submit} disabled={saving} data-testid="dispo-pap-submit">
                        {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Versand erfassen"}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
